import { ReactNode, useEffect, useState } from 'react';
import Box from "@mui/material/Box";
import Footer from "./Footer";
import Header from './Header';
import { Sidebar } from './Sidebar';
import SignalrService from '../../services/signalrService';
import { Alert, Link, Snackbar } from '@mui/material';
import InfoAlert from './InfoAlert';
import { ROUTES } from '../../constants/routes';
import { SignalrEvents } from "../../models/enums/signalrEvents";
import { notificationsService } from '../../services/notificationsService';
import { DesktopNotificationDto } from '../../models/notifications/desktopNotificationDto';
import { permissionService } from '../../services/permissionService';
import { RoleType } from '../../models/enums/roleType';
import { DesktopNotificationsModal } from './DesktopNotificationsModal';
import { useBranding } from '../../contexts/BrandingContext';
import { notificationSoundService } from '../../services/notificationSoundService';

interface AppLayoutProps {
  title: string;
  children: ReactNode;
  hasReturnButton?: boolean;
}

export const Layout: React.FC<AppLayoutProps> = ({ title, children, hasReturnButton }) => {
  const { refreshBranding } = useBranding();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [message, setMessage] = useState<ReactNode>(null);
  const [originalDocumentTitle] = useState<string>(document.title);
  const [count, setCount] = useState<number>(0);
  const [blinkIntervalId, setBlinkIntervalId] = useState<NodeJS.Timeout | null>(null);
  const { subscribeEvent, unsubscribeEvent } = SignalrService();
  const [desktopNotifications, setDesktopNotifications] = useState<DesktopNotificationDto[]>([]);
  const [bellOpen, setBellOpen] = useState(false);
  const [toastQueue, setToastQueue] = useState<DesktopNotificationDto[]>([]);
  const [currentToast, setCurrentToast] = useState<DesktopNotificationDto | null>(null);

  useEffect(() => {
    void refreshBranding();
  }, [refreshBranding]);

  useEffect(() => {
    subscribeEvent(SignalrEvents.NewMedicalCheckupReceived, onNewMedicalCheckupReceived);
    if (permissionService.hasRole(RoleType.Admin) || permissionService.hasRole(RoleType.Medic)) {
      loadDesktopNotifications();
    }

    const onTabVisibilityChange = () => {
      if (!document.hidden) { // user open tab
        stopBlinking();
      } else { // user leave tab
        setCount(0);
      }
    };

    document.addEventListener("visibilitychange", onTabVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onTabVisibilityChange);
      stopBlinking();
      unsubscribeEvent(SignalrEvents.NewMedicalCheckupReceived, onNewMedicalCheckupReceived);
    };
  }, []);

  useEffect(() => {
    if (count > 0) {
      if (document.hidden) {
        startBlinking();
      }
    } else {
      stopBlinking();
    }
  }, [count]);

  useEffect(() => {
    if (!currentToast && toastQueue.length > 0) {
      setCurrentToast(toastQueue[0]);
    }
  }, [toastQueue, currentToast]);

  const startBlinking = () => {
    if (blinkIntervalId) clearInterval(blinkIntervalId);

    const message = `(${count}) Новые осмотры в очереди`;
    let isShown = true;
    let newBlinkIntervalId = setInterval(() => {
      document.title = isShown ? message : originalDocumentTitle;
      isShown = !isShown;
    }, 1000);
    setBlinkIntervalId(newBlinkIntervalId);
  };

  const stopBlinking = () => {
    setCount(0);

    if (blinkIntervalId) {
      clearInterval(blinkIntervalId);
      setBlinkIntervalId(null);
    }

    document.title = originalDocumentTitle;
  };

  const onNewMedicalCheckupReceived = (_medicalCheckupId: number) => {
    const url = permissionService.hasRole(RoleType.Medic)
      ? ROUTES.medicalCheckups.queue
      : ROUTES.medicalCheckups.history;
    const messageElement = (
      <span>
        <Link href={url}>Новый осмотр доступен в очереди</Link>
      </span>
    );
    setMessage(messageElement);
    setCount(prevCount => prevCount + 1);
    playSound();
  };

  const loadDesktopNotifications = async () => {
    try {
      const unread = await notificationsService.getUnread();
      setDesktopNotifications(unread);
      setToastQueue(unread);
    } catch (e) {
      console.error(e);
    }
  };

  const handleBellClick = () => {
    setBellOpen(true);
  };

  const handleNotificationRead = async () => {
    if (desktopNotifications.length === 0) {
      setBellOpen(false);
      return;
    }

    const [current, ...rest] = desktopNotifications;
    
    // Закрываем диалог с анимацией
    setBellOpen(false);
    
    try {
      await notificationsService.markAsRead(current.id);
      setDesktopNotifications(rest);
      setToastQueue(prev => prev.filter(x => x.id !== current.id));
      
      // Открываем диалог снова с новым уведомлением после завершения анимации закрытия
      setTimeout(() => {
        if (rest.length > 0) {
          setBellOpen(true);
        }
      }, 250);
    } catch (e) {
      console.error(e);
      // В случае ошибки открываем диалог обратно
      setBellOpen(true);
    }

  };

  const handleToastClose = () => {
    setToastQueue(prev => prev.slice(1));
    setCurrentToast(null);
  };

  const playSound = () => {
    void notificationSoundService.play();
  };

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh" sx={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden', bgcolor: 'background.default' }}>
      
      <Header
        title={title}
        hasReturnButton={hasReturnButton}
        notificationCount={desktopNotifications.length}
        onBellClick={handleBellClick}
        onMenuClick={() => setMobileMenuOpen(true)}
      />
      {message && (
        <InfoAlert
          message={message}
          onClose={() => setMessage(null)}
          style={{ marginLeft: 264 }}
        />
      )}
      <Box display="flex" flexGrow={1} sx={{ overflowX: 'hidden', width: '100%', maxWidth: '100vw' }}>
        <Sidebar mobileOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
        <Box component="main" sx={{
          flexGrow: 1,
          ml: { sm: '264px' },
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 2.5, sm: 3 },
          width: 0,
          minWidth: 0,
          maxWidth: '100%',
          overflowX: 'hidden',
          '& > *': { width: '100%', maxWidth: 1560, mx: 'auto' },
        }}>
          {children}
        </Box>
      </Box>
      <Footer />

      <DesktopNotificationsModal
        open={bellOpen}
        notifications={desktopNotifications}
        onClose={() => setBellOpen(false)}
        onNotificationRead={handleNotificationRead}
      />

      {currentToast && (
        <Snackbar
          open={true}
          onClose={handleToastClose}
          autoHideDuration={10000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity="info"
            onClose={handleToastClose}
            sx={{ width: '100%', whiteSpace: 'pre-line' }}
          >
            {currentToast.text}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};
